import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import type { SendBlockReason, SendDestinationVM, SendProvider, SendToVM } from './types';

// Send to: pick a connected destination, see who sees what and exactly what
// leaves Poneva, then Review and send. The approval itself is a Poneva card the
// SHARE/SES path raises; this panel never sends and has no standing approval.
// Destinations are chosen by opaque id, never by display name. An `unknown`
// outcome never offers a retry.

export interface SendToProps {
  vm: SendToVM;
  onSelect?: (destinationId: string) => void;
  onSend?: (destinationId: string) => void;
  /** Ask for a new, link-only request. The downgrade is never inferred. */
  onLinkOnly?: (destinationId: string) => void;
  onRetry?: () => void;
}

const PROVIDER_KEY: Record<
  SendProvider,
  'sendProviderSlack' | 'sendProviderTeams' | 'sendProviderEmail'
> = {
  slack: 'sendProviderSlack',
  teams: 'sendProviderTeams',
  email: 'sendProviderEmail',
};
const BLOCK_KEY: Record<SendBlockReason, string> = {
  not_authorized: 'sendBlockedNotAuthorized',
  unsupported_destination: 'sendBlockedUnsupported',
  approval_invalid: 'sendBlockedApproval',
  content_changed: 'sendBlockedChanged',
  dependency_unavailable: 'sendBlockedDependency',
};

export function SendTo({ vm, onSelect, onSend, onLinkOnly, onRetry }: SendToProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const selected = vm.destinations.find((d) => d.id === vm.selectedId) ?? null;
  // Waiting for approval, or delivery unknown: never a second send until it settles.
  const busy = vm.outcome.status === 'waiting_approval' || vm.outcome.status === 'unknown';
  const attachmentBlocked = vm.withAttachment && selected !== null && !selected.attachments;
  return (
    <section className="ap-panel" aria-labelledby="ap-send-title" data-panel="send-to">
      <header className="ap-head">
        <h2 id="ap-send-title">{copy.sendTitle}</h2>
        <p>{copy.sendIntro}</p>
      </header>
      {vm.load === 'loading' && <LoadingState label={copy.sendLoading} />}
      {vm.load === 'error' && <ErrorState message={copy.sendErrorBody} onRetry={onRetry} />}
      {vm.load === 'ready' && vm.destinations.length === 0 && (
        <EmptyState icon="connections" title={copy.sendTitle} description={copy.sendNone} />
      )}
      {vm.load === 'ready' && vm.destinations.length > 0 && (
        <>
          <ul className="ap-list" role="radiogroup" aria-label={copy.sendTitle}>
            {vm.destinations.map((destination) => (
              <Destination
                key={destination.id}
                destination={destination}
                checked={destination.id === vm.selectedId}
                onSelect={() => onSelect?.(destination.id)}
              />
            ))}
          </ul>
          {selected && (
            <>
              <p className="ap-note">{audienceLine(copy, selected)}</p>
              <div className="ap-preview" aria-label={copy.sendPreview}>
                <p className="ap-eyebrow">{copy.sendPreview}</p>
                <strong>{vm.preview.title}</strong>
                <p>{vm.preview.summary}</p>
                <span className="ap-linkcard">{vm.preview.linkLabel}</span>
              </div>
              <p className="ap-note">
                {vm.withAttachment ? copy.sendLeavesAttachment : copy.sendLeaves}
                {vm.scheduled && ` ${copy.sendScheduled}`}
              </p>
              {attachmentBlocked && (
                <Callout
                  tone="warning"
                  actions={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onLinkOnly?.(selected.id)}
                    >
                      {copy.sendLinkOnly}
                    </Button>
                  }
                >
                  {copy.sendFileUnsupported}
                </Callout>
              )}
            </>
          )}
          <Outcome vm={vm} />
          <div className="ap-actions">
            <Button
              type="button"
              disabled={!selected || busy || attachmentBlocked}
              onClick={() => selected && onSend?.(selected.id)}
            >
              {copy.sendAction}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

function audienceLine(
  copy: Readonly<Record<string, string>>,
  destination: SendDestinationVM,
): string {
  const { outsideAudience, audienceCount } = destination;
  // Only an authoritative count is stated; otherwise the general line.
  if (outsideAudience !== null && outsideAudience > 0 && audienceCount !== null)
    return formatCopy(copy.sendAudienceAuthoritative, {
      destination: destination.title,
      outside: String(outsideAudience),
      total: String(audienceCount),
    });
  return copy.sendAudienceGeneric;
}

function Destination({
  destination,
  checked,
  onSelect,
}: {
  destination: SendDestinationVM;
  checked: boolean;
  onSelect: () => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const provider = copy[PROVIDER_KEY[destination.provider]];
  return (
    <li>
      <label className="ap-option-row">
        <input type="radio" name="send-destination" checked={checked} onChange={onSelect} />
        <span className="ap-row-copy">
          <strong>
            {destination.title}
            {destination.scheduleLabel && <StatusPill>{destination.scheduleLabel}</StatusPill>}
          </strong>
          <span>
            {destination.audienceCount !== null
              ? formatCopy(copy.sendDestinationLine, {
                  provider,
                  count: String(destination.audienceCount),
                })
              : formatCopy(copy.sendDestinationLineUnknown, { provider })}
          </span>
        </span>
      </label>
    </li>
  );
}

function Outcome({ vm }: { vm: SendToVM }) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const { outcome } = vm;
  const name = (id: string) => vm.destinations.find((d) => d.id === id)?.title ?? '';
  switch (outcome.status) {
    case 'idle':
      return null;
    case 'waiting_approval':
      return <Callout tone="info">{copy.sendWaiting}</Callout>;
    case 'delivered':
      return (
        <Callout tone="success">
          {formatCopy(copy.sendDelivered, { destination: name(outcome.destinationId) })}
        </Callout>
      );
    case 'failed_unsent':
      return <Callout tone="danger">{copy.sendFailed}</Callout>;
    case 'unknown':
      return (
        <Callout tone="warning">
          {formatCopy(copy.sendUnknown, { destination: name(outcome.destinationId) })}
        </Callout>
      );
    case 'blocked':
      return (
        <Callout tone="danger">{copy[BLOCK_KEY[outcome.reason] as keyof typeof copy]}</Callout>
      );
  }
}
